import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export function PrivacyPolicyPage() {
  const effectiveDate = import.meta.env.VITE_PRIVACY_POLICY_DATE || "July 2026";

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to platform
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          <p className="text-gray-600 mt-2">Effective Date: {effectiveDate}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-10 text-gray-700">
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Information We Collect</h2>
          <p className="mb-3">
            Yansar Consulting Services ("we," "our," or "us") collects information necessary to provide our financial analysis platform. This includes:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Uploaded financial documents:</strong> Bank statements, tax returns, profit & loss statements, and other financial records you upload for analysis</li>
            <li><strong>User account information:</strong> Your name, email address, firm name, and professional credentials</li>
            <li><strong>Engagement metadata:</strong> Engagement dates, client information, project scope, and analysis results</li>
            <li><strong>Usage logs:</strong> Information about how you use the platform (login times, features accessed, pages viewed)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">How We Use Your Information</h2>
          <p className="mb-3">We use your information for:</p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>AI-assisted financial analysis:</strong> Processing uploaded documents through Anthropic Claude API for pattern recognition, categorization, and insights</li>
            <li><strong>Platform functionality:</strong> Enabling engagement tracking, user authentication, and report generation</li>
            <li><strong>Security monitoring:</strong> Detecting and preventing unauthorized access, fraud, and abuse</li>
            <li><strong>Service improvement:</strong> Understanding how features are used to prioritize enhancements</li>
          </ul>
          <p className="mt-3 text-sm text-gray-600">
            <strong>Important:</strong> Your data is NOT used to train or fine-tune AI models. Per our agreement with Anthropic, customer data shared with the Claude API is never used for model training or improvement.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Data Sharing and Sub-Processors</h2>
          <p className="mb-4">We may share your information with the following service providers:</p>
          <table className="w-full border border-gray-300 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-3 text-left font-semibold">Service Provider</th>
                <th className="border border-gray-300 p-3 text-left font-semibold">Purpose</th>
                <th className="border border-gray-300 p-3 text-left font-semibold">Data Policy</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 p-3">Anthropic PBC</td>
                <td className="border border-gray-300 p-3">AI inference and financial analysis</td>
                <td className="border border-gray-300 p-3">No training on customer data</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 p-3">Microsoft Azure</td>
                <td className="border border-gray-300 p-3">Database storage and blob storage</td>
                <td className="border border-gray-300 p-3">Enterprise data residency agreements</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">Render</td>
                <td className="border border-gray-300 p-3">Compute infrastructure and hosting</td>
                <td className="border border-gray-300 p-3">Standard data protection terms</td>
              </tr>
            </tbody>
          </table>
          <p className="mt-4 text-sm text-gray-600">
            <strong>We do not sell your data</strong> to third parties, period. We do not allow third parties to use your data for their own purposes.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Data Retention</h2>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Engagement data (documents, analyses):</strong> Retained for 90 days after engagement close, then automatically deleted</li>
            <li><strong>Account data:</strong> Retained while your account is active. Deleted 30 days after account termination</li>
            <li><strong>Tax consent records:</strong> Retained for 7 years (required by IRS for §7216 recordkeeping)</li>
            <li><strong>Audit logs:</strong> Retained for 3 years for security and compliance auditing</li>
            <li><strong>AI inference logs:</strong> Anthropic does not retain your inputs or outputs</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Your Rights (California CCPA/CPRA)</h2>
          <p className="mb-3">If you are a California resident, you have the right to:</p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Know:</strong> What personal information we collect, use, and share</li>
            <li><strong>Delete:</strong> Request deletion of your data (subject to legal retention requirements)</li>
            <li><strong>Opt-out of sale:</strong> We don't sell your data, but you have this right under law</li>
            <li><strong>Non-discrimination:</strong> You will not face discrimination for exercising your privacy rights</li>
          </ul>
          <p className="mt-4 text-sm">
            To exercise these rights, contact us at{" "}
            <a href="mailto:privacy@yansarconsulting.com" className="text-blue-600 hover:underline">
              privacy@yansarconsulting.com
            </a>
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Your Rights (EU/GDPR)</h2>
          <p className="mb-3">If you are in the European Union, you have additional rights under GDPR:</p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Erasure:</strong> Request deletion of your data (right to be forgotten)</li>
            <li><strong>Portability:</strong> Request your data in a portable format</li>
            <li><strong>Restriction:</strong> Request restriction of processing</li>
            <li><strong>Objection:</strong> Object to processing of your data</li>
          </ul>
          <p className="mt-4 text-sm">
            Our EU Representative is available at{" "}
            <a href="mailto:privacy@yansarconsulting.com" className="text-blue-600 hover:underline">
              privacy@yansarconsulting.com
            </a>
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Security</h2>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Encryption at rest:</strong> AES-256 encryption for all data stored in Azure</li>
            <li><strong>Encryption in transit:</strong> TLS 1.2+ for all network communications</li>
            <li><strong>Access controls:</strong> Role-based access controls (RBAC) limit who can access data</li>
            <li><strong>Breach notification:</strong> In the unlikely event of a breach, we will notify affected account administrators within 48 hours</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or our privacy practices, contact us at:
          </p>
          <div className="mt-4 text-sm">
            <p><strong>Privacy Team</strong></p>
            <p><a href="mailto:privacy@yansarconsulting.com" className="text-blue-600 hover:underline">privacy@yansarconsulting.com</a></p>
            <p className="mt-2">Yansar Consulting Services</p>
          </div>
        </section>

        <div className="border-t border-gray-300 pt-6 text-xs text-gray-500">
          <p>Last updated: {effectiveDate}</p>
        </div>
      </div>
    </div>
  );
}
